import invariant from 'invariant';

import * as CardAbilities from './CardAbilities';
import * as GameConstants from './GameConstants';
import { Phase } from './Phases';
import * as TributeLogic from './TributeLogic';
import * as Utility from './Utility';

import nullthrows from 'nullthrows';
import { match } from 'ts-pattern';
import { FAVOR_PER_TRIBUTE_TOKEN } from './GameConstants';
import { EventTypes, GameEventData, TriggeredCardInfo } from './GameEvents';
import { CardDef, GameComputedState, TributeDef } from './GameModel';
import { GameOptions, TokenCapType } from './GameOptions';
import { Token } from './Resources';

export type CardWithID = CardDef & { id: string };
export type TributeCardWithID = TributeDef & {
  id: string;
  defID: string;
};

export interface Bid {
  tradeRowIndex: number;
  military: number;
}
export type ResolutionSelection = {
  cardIDToGain: string;
};

export type ContextPlayer = {
  userID: string;
  cards: CardWithID[];
  counters: Utility.CounterDelta;
  bid: Bid | null;
  selectedCard: CardWithID | null;
};

export interface Context {
  player: ContextPlayer;
  turn: number;
  age: number;

  options: GameOptions;

  // counters produced this turn
  production_delta: Utility.CounterDelta;
  // counters spent this turn
  spent_delta: Utility.CounterDelta;

  // Informational only
  // counters refunded this turn
  refund_delta: Utility.CounterDelta;
  // counters generated by drafted card
  card_delta: Utility.CounterDelta;
  // counters generated by triggered cards
  triggerDelta: Utility.CounterDelta;

  tribute_favor: number;

  completedTributeIDs: string[];
  triggeredCards: TriggeredCardInfo[];
}

export function makeContext(
  player: ContextPlayer,
  turn: number,
  age: number,
  options: GameOptions,
): Context {
  return {
    player,
    turn,
    age,

    options,

    spent_delta: Utility.makeCounterDelta(),
    production_delta: Utility.makeCounterDelta(),
    refund_delta: Utility.makeCounterDelta(),
    card_delta: Utility.makeCounterDelta(),

    tribute_favor: 0,

    completedTributeIDs: [],

    triggerDelta: Utility.makeCounterDelta(),
    triggeredCards: [],
  };
}
export function makeContextFromGame(
  player: ContextPlayer,
  game: GameComputedState,
): Context {
  return makeContext(player, game.turn, game.age, game.options);
}

export function computeBaseCounters(context: Context): Utility.CounterDelta {
  let ret = Utility.makeCounterDelta();
  context.player.cards.forEach((card) => {
    const baseAbility = CardAbilities.getBaseCountersAbility(card);
    if (!baseAbility) {
      return;
    }
    Utility.applyCounterDelta(ret, baseAbility.getBaseCounters(context));
  });
  return ret;
}

export type CanAffordCardResult =
  | {
      canAfford: true;
    }
  | {
      canAfford: false;
      reason: string;
    };
export function canAffordCard(
  context: Context,
  card: CardDef,
): CanAffordCardResult {
  const baseCounters = computeBaseCounters(context);
  if (card.cost > context.player.counters.gold + baseCounters.gold) {
    return {
      canAfford: false,
      reason: 'Not enough gold',
      // reason: 'Insufficient gold to buy card.',
    };
  }

  const additionalCosts = CardAbilities.getAdditionalCosts(card);
  if (additionalCosts?.warTokens) {
    if (additionalCosts.warTokens > context.player.counters.warTokens) {
      return {
        canAfford: false,
        reason: 'Not enough Clash tokens',
        // reason: 'Insufficient war tokens to buy card.',
      };
    }
  }

  return {
    canAfford: true,
  };
}

export function handleBid(
  context: Context,
  bid: Bid,
  gameState: GameComputedState,
): { context: Context; events: GameEventData[] } {
  const baseCounters = computeBaseCounters(context);
  context.spent_delta.military += Math.max(
    0,
    bid.military - baseCounters.military,
  );

  const card = gameState.table[bid.tradeRowIndex];

  return {
    context,
    events: [
      {
        type: EventTypes.BID,
        payload: {
          userID: context.player.userID,
          bid,
          spentMilitary: context.spent_delta.military,
          cardID: card.id,
        },
      },
    ],
  };
}

export function handlePartialMilitaryRefund(context: Context): Context {
  // Refund half of military spent
  const new_context = { ...context };
  const spent_military = Math.max(
    0,
    nullthrows(context.player.bid).military -
      computeBaseCounters(context).military,
  );
  const refunded_military = Math.floor(spent_military / 2);

  new_context.refund_delta.military += refunded_military;
  new_context.production_delta.military += refunded_military;

  return new_context;
}

export function handleFullMilitaryRefund(context: Context): Context {
  // Refund all military spent
  const new_context = { ...context };
  const spent_military = Math.max(
    0,
    nullthrows(context.player.bid).military -
      computeBaseCounters(context).military,
  );
  const refunded_military = spent_military;

  new_context.refund_delta.military += refunded_military;
  new_context.production_delta.military += refunded_military;

  return new_context;
}

export function handleHigherBidMilitaryRefund(
  context: Context,
  winningBidMilitary: number,
): Context {
  // Refund military spent equal to the winning player's bid
  const new_context = { ...context };
  const player = context.player;
  const base_military = computeBaseCounters(context).military;
  const refund_military = Math.max(
    0,
    nullthrows(player.bid).military -
      Math.max(winningBidMilitary, base_military),
  );

  new_context.refund_delta.military += refund_military;
  new_context.production_delta.military += refund_military;

  return new_context;
}
export function handleGainCard(
  context: Context,
  gainedCard: CardWithID,
): {
  context: Context;
  events: GameEventData[];
} {
  const new_context = { ...context };
  new_context.card_delta.favor += gainedCard.favor;

  // Drafted card "on gain" favor — computed excluding the gained card itself
  const onGainAbility = CardAbilities.getGainSelfCardAbility(gainedCard);
  if (onGainAbility) {
    const contextBeforeGain: Context = {
      ...new_context,
      player: {
        ...new_context.player,
        cards: new_context.player.cards.filter((c) => c.id !== gainedCard.id),
      },
    };
    const output = onGainAbility.onGainSelf(contextBeforeGain);
    Utility.applyCounterDelta(new_context.card_delta, output);
  }

  Utility.applyCounterDelta(
    new_context.production_delta,
    new_context.card_delta,
  );

  // Gained card's prophecy (end-of-game effect) - include in cardCounters for animation
  // but not in counters (favor is fully computed, not stored)
  const cardCounters = { ...new_context.card_delta };
  const gainedCardProphecy = CardAbilities.getEndOfGameCardAbility(gainedCard);
  if (gainedCardProphecy) {
    const output = gainedCardProphecy.onEndOfGame(new_context);
    Utility.applyCounterDelta(cardCounters, output);
  }

  // Existing cards "on gain card"
  const triggeredCards: TriggeredCardInfo[] = [];
  let triggerDelta = Utility.makeCounterDelta();
  new_context.player.cards.forEach((card) => {
    if (card === gainedCard) {
      return;
    }
    const onGainAbility = CardAbilities.getGainOtherCardAbility(card);
    if (onGainAbility) {
      const output = onGainAbility.onGainCard(context, gainedCard);
      if (!Utility.isZeroCounterDelta(output)) {
        new_context.triggeredCards.push({
          cardID: card.id,
          counters: output,
        });

        triggeredCards.push({
          cardID: card.id,
          counters: output,
        });
      }
      Utility.applyCounterDelta(triggerDelta, output);
    }
  });

  // Existing prophecy cards - compute delta from gaining this card
  const contextWithoutGainedCard: Context = {
    ...new_context,
    player: {
      ...new_context.player,
      cards: new_context.player.cards.filter((c) => c.id !== gainedCard.id),
    },
  };
  new_context.player.cards.forEach((card) => {
    if (card === gainedCard) {
      return;
    }
    const prophecyAbility = CardAbilities.getEndOfGameCardAbility(card);
    if (prophecyAbility) {
      const valueWith = prophecyAbility.onEndOfGame(new_context);
      const valueWithout = prophecyAbility.onEndOfGame(
        contextWithoutGainedCard,
      );
      const delta = Utility.subtractCounterDeltas(valueWith, valueWithout);
      if (!Utility.isZeroCounterDelta(delta)) {
        triggeredCards.push({
          cardID: card.id,
          counters: Utility.compressCounterDelta(delta),
        });
      }
    }
  });

  Utility.applyCounterDelta(new_context.triggerDelta, triggerDelta);
  Utility.applyCounterDelta(new_context.production_delta, triggerDelta);

  return {
    context: new_context,
    events: [
      {
        type: EventTypes.GAIN_CARD,
        payload: {
          userID: new_context.player.userID,
          cardID: gainedCard.id,
          counters: extractCounterDeltaFromContext(new_context),

          cardCounters: Utility.compressCounterDelta(cardCounters),
          triggeredCards,
        },
      },
    ],
  };
}

export function handleResolutionSelection(
  context: Context,
  draftedCard: CardWithID,
  cardToGain: CardWithID,
): {
  context: Context;
  events: GameEventData[];
} {
  const new_context = { ...context };

  const baseCounters = computeBaseCounters(context);

  // Pay for card
  new_context.spent_delta.gold += Math.max(
    0,
    cardToGain.cost - baseCounters.gold,
  );
  const additionalCosts = CardAbilities.getAdditionalCosts(cardToGain);
  if (additionalCosts?.warTokens) {
    new_context.spent_delta.warTokens += additionalCosts.warTokens;
  }

  // Existing cards "on draft card"
  context.player.cards.forEach((card) => {
    const onDraftAbility = CardAbilities.getDraftCardAbility(card);
    if (onDraftAbility) {
      const output = onDraftAbility.onDraftCard(context, draftedCard);
      Utility.applyCounterDelta(new_context.production_delta, output);
    }
  });

  return {
    context: new_context,
    events: [
      {
        type: EventTypes.PURCHASE_CARD,
        payload: {
          userID: new_context.player.userID,
          cardID: cardToGain.id,
          counters: extractCounterDeltaFromContext(new_context),
          spentCounters: new_context.spent_delta,
          triggeredCards: new_context.triggeredCards,
        },
      },
    ],
  };
}

export function handleProduction(context: Context): Context {
  const new_context = { ...context };

  // Existing cards "on production"
  context.player.cards.forEach((card) => {
    const onProductionAbility = CardAbilities.getEachTurnCardAbility(card);
    if (onProductionAbility) {
      const output = onProductionAbility.onTurn(context);
      Utility.applyCounterDelta(new_context.production_delta, output);
    }
  });

  return new_context;
}

export function handleTokenGain(
  context: Context,
  token: Token,
): GameEventData[] {
  // Existing cards "on gain other"
  const triggeredCards: TriggeredCardInfo[] = [];
  context.player.cards.forEach((card) => {
    const output = CardAbilities.getGainOtherCardAbility(card)?.onGainToken(
      context,
      token,
    );
    if (output && !Utility.isZeroCounterDelta(output)) {
      triggeredCards.push({
        cardID: card.id,
        counters: output,
      });
    }
  });

  return [
    {
      type: EventTypes.EARN_TOKEN,
      payload: {
        userID: context.player.userID,
        token,
        counters: extractCounterDeltaFromContext(context),
        triggeredCards,
      },
    },
  ];
}

export function handleTributeGain(
  context: Context,
  tributes: TributeCardWithID[],
  game: GameComputedState,
  turnEvents: GameEventData[],
): {
  context: Context;
  events: GameEventData[];
} {
  let check_tributes = true;
  let new_context = context;

  while (check_tributes) {
    check_tributes = false;
    tributes.forEach((tribute) => {
      if (new_context.completedTributeIDs.includes(tribute.id)) {
        return;
      }

      const tributeLogic = TributeLogic.getTributeLogic(tribute);
      invariant(tributeLogic, 'no card function for tribute %s', tribute.name);

      const planningData = game.planningStateByTributeID[tribute.id] ?? null;
      if (
        tributeLogic.shouldGainCard(new_context, {
          gameState: game,
          turnEvents: turnEvents,
          planningState: planningData,
        })
      ) {
        new_context = { ...new_context };

        const delta = tributeLogic.onGainCounterDelta(new_context);
        Utility.applyCounterDelta(new_context.production_delta, delta);

        new_context.completedTributeIDs.push(tribute.id);
        new_context.tribute_favor += delta.favor;
        check_tributes = true;

        // Existing cards "on gain tribute card"
        context.player.cards.forEach((card) => {
          const output =
            CardAbilities.getGainOtherCardAbility(card)?.onGainTribute(
              new_context,
            );
          if (output && !Utility.isZeroCounterDelta(output)) {
            Utility.applyCounterDelta(new_context.production_delta, output);
            Utility.applyCounterDelta(new_context.triggerDelta, output);
            new_context.triggeredCards.push({
              cardID: card.id,
              counters: output,
            });
          }
        });
      }
    });
  }

  const events: GameEventData[] = [];
  if (new_context.completedTributeIDs.length > 0) {
    events.push({
      type: EventTypes.GAIN_TRIBUTES,
      payload: {
        userID: new_context.player.userID,
        counters: extractCounterDeltaFromContext(new_context),

        favor: new_context.tribute_favor,
        tributeTokens: new_context.production_delta.tributeTokens,
        tributeIDs: new_context.completedTributeIDs,
        triggeredCards: new_context.triggeredCards,
      },
    });
  }

  return {
    context: new_context,
    events,
  };
}

export function computeEndOfGameEffectsFavor(context: Context): {
  favor: number;
  triggeredCards: TriggeredCardInfo[];
  context: Context;
} {
  const initial_favor_delta = context.production_delta.favor;
  let newContext = handleEndOfGameEffects(context);
  return {
    favor: newContext.production_delta.favor - initial_favor_delta,
    triggeredCards: newContext.triggeredCards,
    context: newContext,
  };
}

export type PlayerFavor = {
  cardFavor: number;
  tributeFavor: number;
  prophecyFavor: number;
  prophecyCardIDs: string[];
};

export function computePlayerFavorDetails(
  player: ContextPlayer,
  context: Context,
): PlayerFavor {
  const cardFavor = player.cards.reduce((acc, card) => {
    if (card.favor) {
      return acc + card.favor;
    }
    return acc;
  }, 0);
  const tributeFavor =
    (player.counters.tributeTokens || 0) * FAVOR_PER_TRIBUTE_TOKEN;
  const prophecyFavorDetails = computeEndOfGameEffectsFavor(context);
  const prophecyFavor = prophecyFavorDetails.favor;
  const prophecyCardIDs = prophecyFavorDetails.triggeredCards.map(
    (x) => x.cardID,
  );

  return {
    cardFavor,
    tributeFavor,
    prophecyFavor,
    prophecyCardIDs,
  };
}

export function endOfGamePointsPerToken(gameOptions: GameOptions): {
  gold: number;
  military: number;
} {
  return {
    gold: 5,
    military: 5,
  };
}

export function handleEndOfGameEffects(context: Context): Context {
  const new_context = { ...context };

  context.player.cards.forEach((card) => {
    const onEndOfGameAbility = CardAbilities.getEndOfGameCardAbility(card);
    if (onEndOfGameAbility) {
      const output = onEndOfGameAbility.onEndOfGame(context);

      new_context.triggeredCards.push({
        cardID: card.id,
        counters: output,
      });
      Utility.applyCounterDelta(new_context.production_delta, output);
    }
  });

  return new_context;
}

export function handleEndGameScoring(context: Context): Context {
  const new_context = { ...context };

  const pointsPerToken = endOfGamePointsPerToken(context.options);

  let goldPerFavor = pointsPerToken.gold;
  let militaryPerFavor = pointsPerToken.military;

  new_context.production_delta.favor += Math.floor(
    new_context.player.counters.gold / goldPerFavor,
  );
  new_context.spent_delta.gold += context.player.counters.gold;

  new_context.production_delta.favor += Math.floor(
    new_context.player.counters.military / militaryPerFavor,
  );
  new_context.spent_delta.military += context.player.counters.military;

  return new_context;
}

export interface GameInfo {
  basicPile: CardWithID[];
  tradeRow: CardWithID[];
  cardsByID: { [id: string]: CardWithID | TributeCardWithID };
  turn: number;
  phase: Phase;
  age: number;
  options: GameOptions;
}

export function validateBid(
  player: ContextPlayer,
  bid: Bid,
  game_info: GameComputedState,
) {
  let { phase } = game_info;

  if (phase !== Phase.PLANNING) {
    throw new Error('You can only bid during the planning phase.');
  }
  const baseCounters = computeBaseCounters(
    makeContext(player, game_info.turn, game_info.age, game_info.options),
  );
  if (
    !Number.isInteger(bid.military) ||
    bid.military < 0 ||
    bid.military > player.counters.military + baseCounters.military
  ) {
    throw new Error(
      `Invalid military bid: ${bid.military}. Max bid: ${player.counters.military + baseCounters.military}`,
    );
  }
  if (
    !Number.isInteger(bid.tradeRowIndex) ||
    bid.tradeRowIndex < 0 ||
    bid.tradeRowIndex > game_info.table.length
  ) {
    throw new Error('Invalid trade row index.');
  }
}

export function validateResolve(
  player: ContextPlayer,
  resolution: ResolutionSelection,
  game_info: GameComputedState,
) {
  let { phase } = game_info;

  if (phase !== Phase.RESOLUTION) {
    throw new Error('You can only resolve during the resolution phase.');
  }

  let cardToGain: CardWithID | null = null;
  const basicCard = game_info.basicPile.find(
    (card) => card.id === resolution.cardIDToGain,
  );
  if (basicCard) {
    cardToGain = basicCard;
  } else {
    const bid = player.bid;
    if (!bid) {
      throw new Error('Missing bid.');
    }

    const bidCard = game_info.table[bid.tradeRowIndex];
    if (
      player.selectedCard?.id !== resolution.cardIDToGain ||
      bidCard.id !== resolution.cardIDToGain
    ) {
      throw new Error('Invalid card selection.');
    }
    cardToGain = bidCard;
  }
  invariant(cardToGain, 'card to gain not found');

  const context = makeContext(
    player,
    game_info.turn,
    game_info.age,
    game_info.options,
  );
  const canAfford = canAffordCard(context, cardToGain);
  if (!canAfford.canAfford) {
    throw new Error(canAfford.reason);
  }
}
export function getMaxCountersForAge(
  age: number,
  tokenCap: TokenCapType,
): {
  maxGold: number;
  maxMilitary: number;
} {
  const clampedAge = Utility.clamp(age, 1, GameConstants.AGES_PER_GAME);
  const [maxGold, maxMilitary] = match(tokenCap)
    .with(TokenCapType.Legacy, TokenCapType.Low, () => [
      GameConstants.MAX_GOLD,
      GameConstants.MAX_MILITARY,
    ])
    .with(TokenCapType.Standard, () => [
      GameConstants.MAX_GOLD_PER_AGE[clampedAge - 1],
      GameConstants.MAX_MILITARY_PER_AGE[clampedAge - 1],
    ])
    .with(TokenCapType.LowMil, () => [
      GameConstants.MAX_GOLD_PER_AGE[clampedAge - 1],
      GameConstants.MAX_MILITARY_LOW_MIL_PER_AGE[clampedAge - 1],
    ])
    .with(TokenCapType.Bump, () => [
      GameConstants.MAX_BUMP_PER_AGE[clampedAge - 1],
      GameConstants.MAX_BUMP_PER_AGE[clampedAge - 1],
    ])
    .exhaustive();
  return {
    maxGold,
    maxMilitary,
  };
}

export function getMaxCounters(context: Context): {
  maxGold: number;
  maxMilitary: number;
} {
  return getMaxCountersForAge(context.age, context.options.tokenCap);
}

export function rollDie(): number {
  return Math.floor(Math.random() * 6) + 1;
}

export function extractCounterDeltaFromContext(
  context: Context,
): Partial<Utility.CounterDelta> {
  const initialCounters = context.player.counters;

  let finalCounters = context.player.counters;
  finalCounters = Utility.subtractCounterDeltas(
    finalCounters,
    context.spent_delta,
  );
  finalCounters = Utility.addCounters(finalCounters, context.production_delta);

  const { maxGold, maxMilitary } = getMaxCounters(context);
  finalCounters.gold = Utility.clamp(finalCounters.gold, 0, maxGold);
  finalCounters.military = Utility.clamp(
    finalCounters.military,
    0,
    maxMilitary,
  );

  return Utility.compressCounterDelta(
    Utility.subtractCounterDeltas(finalCounters, initialCounters),
  );
}
